import React from "react";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { as_title, format_value, toSentenceCase } from "../../utils/utils";
import { InfoCircleFill, ExclamationCircleFill } from "react-bootstrap-icons";

/**
 * Displays a list of items based on the provided fields and data.
 * It maps through the `fields` and renders a `DisplayItem` for each field.
 * @returns {JSX.Element} A row containing `DisplayItem` components.
 */

const DisplayItems = ({ fields, data }) => (
  <Row className="test-details-section">
    {fields.map(field => (
      <DisplayItem
        key={field.name}
        field_name={field.name}
        field={field}
        data={data}
      />
    ))}
  </Row>
);

DisplayItems.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      title: PropTypes.string,
      fmt: PropTypes.string,
      choices: PropTypes.object,
      helpText: PropTypes.string
    })
  ).isRequired,
  data: PropTypes.object.isRequired
};

/**
 * Renders a single display item with its value, label, and optional help text.
 * This component takes a field name and looks up the corresponding value in the data object.
 * It also displays additional details like help text if provided.
 * @returns {JSX.Element} A column containing the display item for a single field.
 */

const DisplayItem = ({ field_name, field, data }) => {
  const value = data[field_name];

  // Skip rendering if field is 'nhs_number' and value is null/undefined/empty
  if (field.name === 'nhs_number' && !value) return null;


  return (
    <Col md={6} className="p-0 m-0">
      <div className="test-details-card">

        <h4 className="mb-1">
          {toSentenceCase(field.label) || toSentenceCase(field.title) || toSentenceCase(as_title(field_name))}
          {(field.name === 'nhs_number') && <ExclamationCircleFill className="m-1 fs-6" color="#004137" /> || (field.name === 'nhs_results') && <InfoCircleFill className="m-1 fs-6" color="#004137" />}
        </h4>
        {
          (field.name === 'nhs_results' || field.name === 'nhs_number') ? (
            <h5 className="font-weight-normal">{value}</h5>
          ) : (
            <h5 className="font-weight-normal">
              {field.name === 'email' ? value : format_value(value, field.fmt, field.choices)}
            </h5>
          )
        }
        {field.helpTextAbove && (
          <small className="text-muted">
            {field.helpTextAbove}
          </small>
        )}
        {field.helpText && (
          <small className="text-muted">{toSentenceCase(field.helpText)}</small>
        )}
      </div>
    </Col>
  );
};

DisplayItem.propTypes = {
  field_name: PropTypes.string.isRequired,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    title: PropTypes.string,
    fmt: PropTypes.string,
    choices: PropTypes.object,
    helpText: PropTypes.string,
    helpTextAbove: PropTypes.string,
  }).isRequired,
  data: PropTypes.object.isRequired
};

export default DisplayItems;
